import { useTranslation } from 'react-i18next';
import type { PrescriptionLensSupplyMode, ValidatedBasketLine } from '@optiflow/types';
import { cn } from '@/utils/cn';

/**
 * What the server says about a variant's stock at this branch, as guidance.
 *
 * Always in words beside the dot. A made-to-order lens is made for the job and holds no stock, so it is never "out of stock". The checkout counts again when the order is confirmed.
 */
export function Availability({
  line,
  supplyMode,
}: {
  line: ValidatedBasketLine;
  supplyMode?: PrescriptionLensSupplyMode;
}) {
  const { t } = useTranslation();

  const retired = line.issues.includes('INACTIVE') || line.issues.includes('UNAVAILABLE');
  const [tone, text] = retired
    ? (['none', t('opticalOrders.stock.retired')] as const)
    : supplyMode === 'MADE_TO_ORDER'
      ? (['made', t('opticalOrders.stock.madeToOrder')] as const)
      : line.availableQuantity > 0
        ? (['in', t('opticalOrders.stock.inBranch', { count: line.availableQuantity })] as const)
        : (['none', t('opticalOrders.stock.noneInBranch')] as const);

  return (
    <p className="flex items-center gap-2 text-xs">
      <span
        aria-hidden
        className={cn(
          'size-2 shrink-0 rounded-full',
          tone === 'in' && 'bg-emerald-500',
          tone === 'made' && 'bg-brand-500',
          tone === 'none' && 'bg-amber-500',
        )}
      />
      <span className={tone === 'none' ? 'text-amber-800' : 'text-ink'}>{text}</span>
    </p>
  );
}
